import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../AppContext";
import { formatAsEuro } from "../../helpers/helpers";
import {
  useCostCalculator,
  useFeeCalculator,
  Calculator,
  Section,
} from "../../helpers/price-calculation";
import { ProductTypes } from "../../file.interface";
import { RadioContractDuration } from "../../components/radio-buttons/radios-contract-duration";
import { ScreenContainer } from "../../components/containers";
//
const initialParams: Calculator = {
  averageTransactionAmount: 0,
  processPerMonth: 0,
  numberOfCardMachines: 1,
};

const EstimationScreen8: React.FC = () => {
  //
  const {
    sites,
    selectedPOSSolution,
    selectedContractDuration,
    selectedDeliveryPackage,
  } = useAppContext();
  const navigate = useNavigate();

  const feeCalculator = useFeeCalculator(initialParams);
  const {
    consumerDebitCharge,
    consumerCreditCharge,
    businessCardsCharge,
    pci,
    totalFeeCharge,
  } = feeCalculator;

  const {
    sitesCost,
    deliveryInstallationCost,
    productConfCost,
    monthlyCost,
    oneOffCost,
  } = useCostCalculator();

  const calculateNumberOfCardMachines = () => {
    return sites
      .map((site) =>
        site.cart.reduce(
          (total, item) =>
            item.type === ProductTypes.Terminals ||
            item.type === ProductTypes.Epos
              ? total + 1
              : 0,
          0
        )
      )
      .reduce((grandTotal, itemsPerSite) => itemsPerSite + grandTotal, 0);
  };
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: keyof Calculator
  ) => {
    let inputValue = event.target.value.replace("€", "");

    if (/^[0-9]+$/.test(inputValue) || inputValue === "") {
      const newParams: Calculator = {
        ...feeCalculator.params,
        numberOfCardMachines: calculateNumberOfCardMachines(),
        [field]: inputValue === "" ? 0 : parseInt(inputValue, 10),
      };
      feeCalculator.updateParams(newParams);
    }
  };
  const { averageTransactionAmount, processPerMonth } = feeCalculator.params;

  const grandMonthlyTotal = () => {
    return monthlyCost + totalFeeCharge ?? 0;
  };

  const handleNavigation = () => {
    navigate("/contact-screen-8", {
      state: {
        sitesCost,
        deliveryInstallationCost,
        productConfCost,
        totalFeeCharge,
        averageTransactionAmount,
        processPerMonth,
        selectedContractDuration,
        POSSolutionName: selectedPOSSolution?.POSSolutionName ?? "",
        monthlyTotal: formatAsEuro(grandMonthlyTotal()),
        oneOffCost: formatAsEuro(oneOffCost),
      },
    });
  };

  return (
    <ScreenContainer
      title="Final Step"
      subtitle="Fill in the fields below to discover your transaction fees"
    >
      <div id="calc-container">
        <div className="calc-fields">
          <form action="#" id="calculator" onSubmit={() => false}>
            <label htmlFor="atv">What's your average transaction amount?</label>
            <br />
            <input
              type="text"
              pattern="[0-9]*"
              id="atv"
              name="atv"
              placeholder="€"
              value={
                averageTransactionAmount === 0
                  ? ""
                  : "€" + averageTransactionAmount
              }
              onChange={(e) => handleInputChange(e, "averageTransactionAmount")}
            />
            <br />
            <br />
            <label htmlFor="atm">
              How much do you expect to process through your card machine per
              month?
            </label>
            <br />
            <input
              type="text"
              pattern="[0-9]*"
              id="atm"
              name="atm"
              placeholder="€"
              value={processPerMonth === 0 ? "" : "€" + processPerMonth}
              onChange={(e) => handleInputChange(e, "processPerMonth")}
            />
          </form>
        </div>

        {processPerMonth > 0 && averageTransactionAmount > 0 && (
          <>
            <RadioContractDuration />
            <div className="calc-table">
              <PrintSection section={sitesCost} />
              {selectedDeliveryPackage && (
                <PrintSection section={deliveryInstallationCost} />
              )}
              <PrintSection section={productConfCost} />
              <>
                <span className="calc-table-subtitle">
                  Card Machine Transaction Fee (estimation)
                </span>
                <div>
                  <div className="calc-table-row">
                    <span className="calc-table-unit">Consumer Debit</span>
                    <span className="calc-table-price-unit">
                      {formatAsEuro(consumerDebitCharge)} monthly
                    </span>
                  </div>
                  <div className="calc-table-row">
                    <span className="calc-table-unit">Consumer Credit</span>
                    <span className="calc-table-price-unit">
                      {formatAsEuro(consumerCreditCharge)} monthly
                    </span>
                  </div>
                  <div className="calc-table-row">
                    <span className="calc-table-unit">Business Cards</span>
                    <span className="calc-table-price-unit">
                      {formatAsEuro(businessCardsCharge)} monthly
                    </span>
                  </div>
                  <div className="calc-table-row">
                    <span className="calc-table-unit">PCI</span>
                    <span className="calc-table-price-unit">
                      {formatAsEuro(pci)} monthly
                    </span>
                  </div>
                </div>
              </>

              <div className="calc-divider" />
              <div>
                <div className="calc-table-row price">
                  <span>Total Monthly Fee:</span>
                  <span id="result">{formatAsEuro(grandMonthlyTotal())}</span>
                </div>
                <div className="calc-table-row price">
                  <span>Total One-Off Fee:</span>
                  <span id="result">{formatAsEuro(oneOffCost)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {processPerMonth > 0 && averageTransactionAmount > 0 && (
        <p className="disclaimer">
          The displayed quote has been calculated based on the pricing details
          of the <i>{selectedPOSSolution?.POSSolutionName} Solution</i>. Please
          be aware that the provided quote is an estimate and actual prices may
          vary based on additional customizations or specific requirements for
          your business.
        </p>
      )}

      <div className="buttons-container">
        <button
          className="app-button previous-button"
          onClick={() => navigate(-1)}
        >
          Previous
        </button>
        {processPerMonth > 0 && averageTransactionAmount > 0 && (
          <button
            className="app-button new-site-button"
            onClick={() => handleNavigation()}
          >
            Send for Consultation
          </button>
        )}
      </div>
    </ScreenContainer>
  );
};

interface PrintSectionProps {
  section: Section[];
}
export const PrintSection: React.FC<PrintSectionProps> = ({ section }) => {
  return (
    <div>
      {section?.map((site, index) => (
        <div key={index}>
          {site.sectionName && (
            <span className="calc-table-title">{site.sectionName}</span>
          )}
          {site?.modules?.map((_module, moduleIndex) => (
            <div key={moduleIndex}>
              {_module.moduleName && (
                <span className="calc-table-subtitle">
                  {_module.moduleName}
                </span>
              )}
              {_module?.units?.map((unit, unitIndex) => (
                <div key={unitIndex} className="calc-table-row">
                  <span className="calc-table-unit">{unit.unitName}</span>
                  <span className="calc-table-price-unit">
                    {unit.formattedPrice}
                  </span>
                </div>
              ))}
            </div>
          ))}
          {site.modules && <div className="calc-divider" />}
        </div>
      ))}
    </div>
  );
};

export default EstimationScreen8;
